/**
 * [2023-07-20.1007 by Brian]
 * Split up into sections.
 *
 * [2023-07-06.0740 by Brian]
 * Updated to a data structure that would work with Intellisense.
 *
 * [2023-07-05.1427 by Brian]
 * Created to store string constants that are used in multiple places.
 */

import dayjs, { Dayjs, isDayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { convertToMS } from '../functions/convertToMS';
import { ScheduleClinicSettings } from './ScheduleDefaults';

dayjs.extend(utc);
dayjs.extend(tz);

export const EHR_TERMS_OF_USE_URL =
  'https://help.chiroup.com/security-privacy/chiroup-ehr-terms-of-use';

export const ChiroUpPatientCommon = {
  notFound: 'Patient not found.',
  notAuthorized: 'You are not authorized to access this patient.',
  insuranceNotFound: 'Patient insurance not found.',
};

export const ChiroUpLoginCommon = {
  invalidCode: 'The code is not correct, please try again.',
  emptyCode: 'The code field is required.',
  connectionFailed:
    'The connection to the authentication server could not be made. This may be temporary.',
  triesExhausted: 'You have exceeded the number of allowed attempts.',
};

export const ChiroUpSchedulingCommon = {
  notAuthorized: 'You are not authorized to access scheduling.',
  notAvailable: 'Scheduling is not available.',
  clinicPreferences: {
    save: {
      success: 'Scheduling preferences were saved.',
      error: 'Scheduling preferences could not be saved.',
    },
  },
  appFullName: 'HealthCom Scheduling',
};

// Discriminator `message` values returned by scheduling save endpoints and
// matched on the frontend to surface a soft confirm dialog (room conflict,
// provider cap). Shared so a typo on either the producing or consuming side is
// a compile error rather than a silently-dropped warning.
export const SchedulingResponseMessages = {
  alreadyBooked: 'alreadyBooked',
  capWarning: 'capWarning',
} as const;

export type SchedulingResponseMessage =
  (typeof SchedulingResponseMessages)[keyof typeof SchedulingResponseMessages];

export const ChiroUpTransactionCommon = {
  billingStarted: `A claim has already been submitted.
     Please exercise care when editing.
     Changes should only be made in response
     to claim errors or feedback from the
     payor to avoid potential discrepancies.`,
};

export const ChiroUpAppointmentCommon = {
  unableToReschedule: 'Unable to reschedule appointment.',
  onlineManagementUnavailable: {
    code: 'ONLINE_BOOKING_DISABLED',
    message: 'This appointment cannot be changed or canceled online. Please contact the clinic.',
  },
  notAuthorized: 'You are not authorized to access this appointment.',
  notFound: 'Appointment not found.',
  cancelFailed: 'The appointment could not be canceled.',
  cannotModify: 'This appointment may not be modified.',
  tooSoon: {
    code: 'TOO_SOON',
    message: 'Appointments must be scheduled more than 15 minutes in advance.',
    friendly:
      'Appointments must be scheduled more than 15 minutes in advance. Please select a different time.',
  },
  noPast: {
    code: 'NO_PAST',
    message: 'Appointments in the past are not supported.',
    friendly:
      'There appears to be a problem with the date. It appears to be in the past. Please try again.',
  },
  noSameDay: {
    code: 'NO_SAME_DAY',
    message: 'The clinic does not allow same-day appointments.',
    friendly:
      'The clinic does not allow same-day appointments. Please change the appointment to a different day.',
  },
  timeUnavailable: {
    code: 'TIME_UNAVAILABLE',
    message: 'Provider is not available at this time.',
    friendly:
      'This time is no longer available. The schedule may have just filled up. Please choose a different time or day.',
  },
  outsideCancelWindow: {
    code: 'TOO_LATE_TO_CANCEL',
    message: 'Cancellation period expired.',
    friendly: (minutes: number) =>
      `The clinic does not allow cancelling an appointment within ${ChiroUpAppointmentCommon.cancellationSlotByMinutes[minutes]} of the appointment time.`,
  },
  cancellationSlotByMinutes: ScheduleClinicSettings.cancellationOptions.reduce((acc, obj) => {
    return { ...acc, [obj.value]: obj.text };
  }, {}) as { [key: number]: string },
};

const commonDisplayDatetime = (
  value?: Date | Dayjs | string | number,
  timezone?: string,
  format?: string,
  skipCleanupDatetime?: boolean
) => {
  if (!value) {
    return '';
  }

  if (isDayjs(value)) {
    return value.format(format || ChiroUpDayJsCommon.format.datetime);
  }

  let formatted: string = '- bad-time -';
  let valueToUse = '';

  try {
    valueToUse = getValueToUse(value) as any;

    formatted = (
      dayjs.isDayjs(valueToUse)
        ? timezone
          ? valueToUse.tz(timezone)
          : valueToUse
        : timezone
          ? dayjs.tz(valueToUse, timezone)
          : dayjs(valueToUse)
    ).format(format || ChiroUpDayJsCommon.format.datetime);
  } catch (e) {
    console.error({
      value,
      valueToUse,
      e,
    });
  }

  // cleanupDatetime's ':00' strip is sugar for 'h:mma' displays (3:00pm →
  // 3pm); on seconds-bearing formats it mangles the value
  // (12:49:00.927 → 12:49.927), so skipCleanupDatetime renders exactly as
  // formatted.
  return skipCleanupDatetime ? formatted : ChiroUpDayJsCommon.cleanupDatetime(formatted);
};

const commonDisplayDatetimeWithDay = (
  value?: Date | Dayjs | string | number,
  timezone?: string,
  format?: string
) => {
  if (!value) {
    return '';
  }

  if (isDayjs(value)) {
    return value.format(format || ChiroUpDayJsCommon.format.datetime);
  }

  let formatted: string = '- bad-time -';
  let valueToUse = '';

  try {
    valueToUse = getValueToUse(value) as any;

    formatted = (
      dayjs.isDayjs(valueToUse)
        ? timezone
          ? valueToUse.tz(timezone)
          : valueToUse
        : timezone
          ? dayjs.tz(valueToUse, timezone)
          : dayjs(valueToUse)
    ).format(format || ChiroUpDayJsCommon.format.datetimeWithDay);
  } catch (e) {
    console.error({
      value,
      valueToUse,
      e,
    });
  }

  return ChiroUpDayJsCommon.cleanupDatetime(formatted);
};

const commonDisplayDate = (
  value?: Date | Dayjs | string | number,
  timezone?: string,
  format?: string
) => {
  if (!value) {
    return '';
  }

  if (isDayjs(value)) {
    return value.format(format || ChiroUpDayJsCommon.format.date);
  }

  let formatted: string = '- bad-date -';
  let valueToUse = '';

  try {
    valueToUse = getValueToUse(value) as any;

    formatted = (
      dayjs.isDayjs(valueToUse)
        ? timezone
          ? valueToUse.tz(timezone)
          : valueToUse
        : timezone
          ? dayjs.tz(valueToUse, timezone)
          : dayjs(valueToUse)
    ).format(format || ChiroUpDayJsCommon.format.date);
  } catch (e) {
    console.error({
      value,
      valueToUse,
      e,
    });
  }

  return ChiroUpDayJsCommon.cleanupDatetime(formatted);
};

export const stripQuotes = (str: string): string => {
  return str.replace(/^"(.*)"$/, '$1');
};

export const ChiroUpDayJsCommon = {
  defaultTimezone: 'America/Chicago',
  format: {
    date: 'MMM D, YYYY',
    isoDate: 'YYYY-MM-DD',
    mmddyyyy: 'MM/DD/YYYY',
    datetime: 'MMM D, YYYY [at] h:mma',
    datetimeWithTz: 'MMM D, YYYY [at] h:mma z',
    datetimeWithDay: 'ddd MMM D, YYYY [at] h:mma',
    timestamp: 'YYYY-MM-DD HH:mm:ss',
    isoAt: 'YYYY-MM-DD [at] h:mma',
    time: 'h:mma',
  },
  now: (fmt?: string) => dayjs().format(fmt ?? ChiroUpDayJsCommon.format.mmddyyyy),
  getAsciiTimestamp: (d?: Date | null | undefined) => {
    if (!d) {
      d = new Date();
    }

    return dayjs(d).format(ChiroUpDayJsCommon.format.timestamp);
  },
  getYyyyMmDd: (d: Date | null | undefined | dayjs.Dayjs, timezone?: string) => {
    if (!d) {
      d = new Date();
    } else if (!timezone && dayjs.isDayjs(d)) {
      return (d as dayjs.Dayjs).format(ChiroUpDayJsCommon.format.isoDate);
    }

    return dayjs(d).tz(timezone).format(ChiroUpDayJsCommon.format.isoDate);
  },
  getMmDdYyyy: (d: Date | number | string | null | undefined | dayjs.Dayjs, timezone?: string) => {
    timezone = timezone ?? ChiroUpDayJsCommon.defaultTimezone;
    const fmt = 'MM/DD/YYYY';

    if (!d) {
      d = new Date();
    } else if (!timezone && dayjs.isDayjs(d)) {
      return (d as dayjs.Dayjs).format(fmt);
    }

    return dayjs(d).tz(timezone).format(fmt);
  },
  dateToString: ({
    d = new Date(),
    fmt = 'MMM D, YYYY',
    tz = 'UTC',
  }: {
    d: Date | null | undefined | dayjs.Dayjs;
    fmt?: string;
    tz?: string;
  }) => {
    return dayjs(d).tz(tz).format(fmt);
  },
  cleanupDatetime: (str: string | undefined) =>
    str &&
    str
      // .replace(/(\d)am\b/g, '$1 a.m.')    // Just for fun, feel free to remove.
      // .replace(/(\d)pm\b/g, '$1 p.m.')    // Just for fun, feel free to remove.
      .replace(/ /g, '\u00A0') // This makes it a non-breaking space.
      .replace(/:00/g, ''), // This gets rid of the :00.
  display: {
    datetime: commonDisplayDatetime,
    datetimeWithDay: commonDisplayDatetimeWithDay,
    datetimeWithTz: (
      value: string | number,
      objectTz: string | undefined,
      userTz: string | undefined,
      format?: string,
      showTimezone?: boolean,
      skipCleanupDatetime?: boolean
    ) => {
      let val = commonDisplayDatetime(value, objectTz, format, skipCleanupDatetime);

      if (objectTz !== userTz && showTimezone) {
        val = `${val} (${objectTz})`;
      }

      return val;
    },
    dateWithTz: (
      value: string | number,
      objectTz: string | undefined,
      userTz: string | undefined,
      format?: string,
      showTimezone?: boolean
    ) => {
      let val = commonDisplayDate(value, objectTz, format);

      if (objectTz !== userTz && showTimezone) {
        val = `${val} (${objectTz})`;
      }

      return val;
    },
    date: (value?: Date | string | number, timezone?: string) => {
      if (!value) {
        return '';
      }

      try {
        const valueToUse = getValueToUse(value),
          formatted: string = (
            timezone ? dayjs.tz(valueToUse, timezone) : dayjs(valueToUse)
          ).format(ChiroUpDayJsCommon.format.date);

        return ChiroUpDayJsCommon.cleanupDatetime(formatted);
      } catch (e: any) {
        return '- bad-date -';
      }
    },
    timeFormat: (value?: string | number, format?: string, timezone?: string) => {
      if (!value) {
        return '';
      }

      let valueToUse = value;

      if (typeof valueToUse === 'string' && !isNaN(Number(valueToUse))) {
        valueToUse = Number(valueToUse);
      }

      // If it is a number and doesn't include milliseconds, add them.
      if (typeof valueToUse === 'number' && valueToUse.toString().length === 10) {
        valueToUse = convertToMS(valueToUse);
      }

      const dayjsValue = timezone ? dayjs.tz(valueToUse, timezone) : dayjs(valueToUse);
      const formatted = dayjsValue.format(format);

      return formatted;
    },
    time: (value?: string | number, timezone?: string) => {
      if (!value) {
        return '';
      }

      let valueToUse = value;

      if (typeof valueToUse === 'string' && !isNaN(Number(valueToUse))) {
        valueToUse = Number(valueToUse);
      }

      // If it is a number and doesn't include milliseconds, add them.
      if (typeof valueToUse === 'number' && valueToUse.toString().length === 10) {
        valueToUse = convertToMS(valueToUse);
      }

      const dayjsValue = timezone ? dayjs.tz(valueToUse, timezone) : dayjs(valueToUse);
      const formatted = dayjsValue.format(ChiroUpDayJsCommon.format.time);

      return ChiroUpDayJsCommon.cleanupDatetime(formatted);
    },
  },
};

/**
 * Created a separate function for this so it could be used in multiple
 * places. This implements the different value formats that it can process.
 *
 * @param value
 * @returns
 */
const getValueToUse = (value?: Date | string | number) => {
  let valueToUse = value;

  if (dayjs.isDayjs(value)) return value;

  /**
   * Try to coerce a Date or string into a number.
   */
  if (typeof value === 'object') {
    try {
      valueToUse = value.getTime();
    } catch (e: any) {
      return `-- ${typeof value} ${value} --`;
    }
  } else if (typeof value === 'string') {
    // Is it a string that represents a number? That is, a 10-digit or 13-digit unixish timestamp?
    const num = Number(value);

    if (!isNaN(num)) {
      if (value.length !== 10 && value.length !== 13) {
        return `-- ${typeof value} ${value} --`;
      } // Probably not what we're looking for in a number.

      valueToUse = num;
    } else {
      // Maybe a date string from MySQL that can be parsed by the Date constructor?
      // Replace space with 'T' for iOS Safari compatibility (requires ISO 8601 format)
      const isoValue = value.replace(' ', 'T');
      valueToUse = new Date(isoValue).getTime();

      if (isNaN(valueToUse)) {
        return `-- ${typeof value} ${value} --`; // Give up.
      }
    }
  }

  // If it is a number and doesn't include milliseconds, add them.
  if (typeof value === 'number' && value.toString().length === 10) {
    valueToUse = convertToMS(value);
  }

  return valueToUse;
};

export const ChiroUpBaseCommon = {
  notAuthorized: 'You are not authorized to access this page.',
  notFound: 'Page not found.',
  format: {
    asMoney: (value?: number | null, currency?: string, decimalPlaces?: number) => {
      currency = currency || 'USD';

      const noDecimalPlaces = decimalPlaces === 0;

      if (currency !== 'USD') {
        return `value?.toFixed(2) ${currency} x`;
      }

      if (typeof value === 'string') {
        try {
          value = Number(value);
        } catch (e: any) {
          // Don't care.
        }
      }

      if (typeof value !== 'number' || isNaN(value)) {
        return `$$0.00 ${typeof value} y`;
      }

      try {
        const signedFixed = `${Number(value).toFixed(noDecimalPlaces ? 0 : decimalPlaces || 2)}`;
        const absoluteFixed = `${Math.abs(Number(value)).toFixed(noDecimalPlaces ? 0 : decimalPlaces || 2)}`;
        const [before, after] = absoluteFixed.split('.');
        const left = Number(before).toLocaleString();
        const isNegativeNonZero = Number(value) < 0 && signedFixed !== '-0.00';

        return isNegativeNonZero
          ? `($${left}${noDecimalPlaces ? '' : '.'}${after || ''} )`
          : `$${left}${noDecimalPlaces ? '' : '.'}${after || ''}`;
      } catch {
        return '$0.00 z';
      }
    },
  },
};
